import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import adminApi from "../../api/adminApi";
import { HUBSPOT_PRODUCT_TYPES } from "../../constants/adminConstants";

const ALL_CAMPUSES = "__all__";

// Split the stored "RESIDENTIAL, AFTERNOON" string back into the multi-select's values.
const parseTypes = (s) =>
  String(s || "")
    .split(",")
    .map((t) => t.trim().toUpperCase())
    .filter((t) => HUBSPOT_PRODUCT_TYPES.includes(t));

const emptyDraft = () => ({
  id: null,
  skuCode: "",
  campus: ALL_CAMPUSES,
  keyword: "",
  productTypes: [],
  notes: "",
});

const draftOf = (row) => ({
  id: row.id,
  skuCode: row.skuCode || "",
  campus: row.campus || ALL_CAMPUSES,
  keyword: row.keyword || "",
  productTypes: parseTypes(row.productTypes),
  notes: row.notes || "",
});

// Body for POST/PUT: the row shape without id/updatedAt. Empty product types → null
// (the import's per-SKU default); "All campuses" → null.
const bodyOf = (d) => ({
  skuCode: d.skuCode.trim().toUpperCase(),
  campus: d.campus === ALL_CAMPUSES ? null : d.campus,
  keyword: d.keyword.trim(),
  productTypes: d.productTypes.length ? d.productTypes.join(", ") : null,
  notes: d.notes.trim() || null,
});

// The friendliest text we can pull out of a 400/409 body.
const errorText = (error, fallback) => {
  const b = error?.body;
  if (b && typeof b === "object") return b.message || b.error || fallback;
  if (typeof b === "string" && b.trim()) return b;
  return error?.message || fallback;
};

// Editable table of SKU → keyword rules for the HubSpot deal import. One row is in edit
// mode at a time (existing or new); everything else stays read-only until it's saved
// or cancelled. Edits take effect on the poller's next pass.
export default function HubspotMappingsTable({ mappings, loading, campuses, onRefresh, setBanner }) {
  const [draft, setDraft] = useState(null); // null | { id: null|number, ... }
  const [saving, setSaving] = useState(false);

  const campusNames = Array.from(
    new Set((campuses || []).map((c) => c.name || c.campusName).filter(Boolean))
  );

  const patch = (p) => setDraft((d) => ({ ...d, ...p }));
  const canSave = !!draft && draft.skuCode.trim() && draft.keyword.trim() && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = bodyOf(draft);
      if (draft.id == null) await adminApi.createHubspotMapping(body);
      else await adminApi.updateHubspotMapping(draft.id, body);
      setDraft(null);
      await onRefresh();
      setBanner({ type: "success", text: `Saved ${body.skuCode} → “${body.keyword}”` });
    } catch (error) {
      const fallback =
        error?.status === 409
          ? "A rule for that SKU and campus already exists"
          : "Failed to save the mapping";
      setBanner({ type: "error", text: errorText(error, fallback) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const where = row.campus || "all campuses";
    if (!window.confirm(`Delete the ${row.skuCode} rule for ${where}?`)) return;
    try {
      await adminApi.deleteHubspotMapping(row.id);
      await onRefresh();
      setBanner({ type: "success", text: `Deleted the ${row.skuCode} rule for ${where}` });
    } catch (error) {
      setBanner({ type: "error", text: errorText(error, "Failed to delete the mapping") });
    }
  };

  const editorRow = (key) => (
    <TableRow key={key} sx={{ bgcolor: "action.hover" }}>
      <TableCell>
        <TextField
          size="small"
          value={draft.skuCode}
          onChange={(e) => patch({ skuCode: e.target.value.toUpperCase() })}
          placeholder="ELE-SPORTS"
          inputProps={{ style: { textTransform: "uppercase" } }}
          autoFocus={draft.id == null}
          sx={{ width: 150 }}
        />
      </TableCell>
      <TableCell>
        <Select
          size="small"
          value={draft.campus}
          onChange={(e) => patch({ campus: e.target.value })}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value={ALL_CAMPUSES}>
            <em>All campuses</em>
          </MenuItem>
          {campusNames.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          value={draft.keyword}
          onChange={(e) => patch({ keyword: e.target.value })}
          placeholder="Sports"
          fullWidth
        />
      </TableCell>
      <TableCell>
        <Select
          size="small"
          multiple
          displayEmpty
          value={draft.productTypes}
          onChange={(e) => patch({ productTypes: e.target.value })}
          renderValue={(sel) =>
            sel.length ? (
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {sel.map((t) => (
                  <Chip key={t} size="small" label={t} />
                ))}
              </Stack>
            ) : (
              <em style={{ opacity: 0.6 }}>Default</em>
            )
          }
          sx={{ minWidth: 180 }}
        >
          {HUBSPOT_PRODUCT_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          value={draft.notes}
          onChange={(e) => patch({ notes: e.target.value })}
          inputProps={{ maxLength: 255 }}
          fullWidth
        />
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
        <Button size="small" onClick={() => setDraft(null)} disabled={saving}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={!canSave}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </TableCell>
    </TableRow>
  );

  if (loading) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" sx={{ opacity: 0.75 }}>
        The import matches each HubSpot SKU to a Greaterbase product whose name contains the
        keyword (case, spacing and dashes ignored). A campus-specific rule overrides the
        all-campuses rule for that campus. Leave product types empty for the import&apos;s
        default (electives: Activity/External; others: any). Week SKUs and INC-WKEND are
        matched by date, not by name, so they need no rule here.
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>SKU</TableCell>
              <TableCell>Campus</TableCell>
              <TableCell>Keyword</TableCell>
              <TableCell>Product types</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((row) =>
              draft && draft.id === row.id ? (
                editorRow(row.id)
              ) : (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ fontFamily: "monospace" }}>{row.skuCode}</TableCell>
                  <TableCell>
                    {row.campus || (
                      <Typography component="span" variant="body2" sx={{ opacity: 0.6 }}>
                        All campuses
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{row.keyword}</TableCell>
                  <TableCell>
                    {parseTypes(row.productTypes).length ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {parseTypes(row.productTypes).map((t) => (
                          <Chip key={t} size="small" label={t} />
                        ))}
                      </Stack>
                    ) : (
                      <Typography component="span" variant="body2" sx={{ opacity: 0.6 }}>
                        Default
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>{row.notes || "—"}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <IconButton
                      size="small"
                      aria-label="Edit mapping"
                      disabled={!!draft}
                      onClick={() => setDraft(draftOf(row))}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Delete mapping"
                      disabled={!!draft}
                      onClick={() => handleDelete(row)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            )}

            {draft && draft.id == null && editorRow("new")}

            {mappings.length === 0 && !draft && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No mapping rules yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={!!draft}
          onClick={() => setDraft(emptyDraft())}
        >
          Add rule
        </Button>
      </Box>
    </Stack>
  );
}
